import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Button, Card, Descriptions, Drawer, Input, InputNumber, Modal, Popconfirm, Segmented, Select, Space, Spin, Switch, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { EyeOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import { dedicatedAdminApi as api, adminTools, isOk, errMsg, arr, STATUS, DC_LABEL } from '../../../shared/services/dedicatedService';
import CountryFlag from '../../../shared/components/CountryFlag';
import DedicatedServerTools from '../../../shared/components/DedicatedServerTools';

const { Text } = Typography;
const money = (v) => (v == null ? '-' : formatMoney(Number(v), PLATFORM_CURRENCY));
const STATUS_ZH = {
    awaiting_payment: '待付款', ordering: '上游下单中', provisioning: '等待交付', active: '运行中', suspended: '已暂停',
    cancel_requested: '待上游取消', cancelled: '已取消', failed: '下单失败',
};
const StatusTag = ({ s }) => <Tag color={(STATUS[s] || ['default'])[0]}>{STATUS_ZH[s] || s}</Tag>;

/** 一台服务器的详情 + 全部管理操作 */
const ServerDrawer = ({ id, onClose, onChanged }) => {
    const [d, setD] = useState(null);
    const [pw, setPw] = useState(null);
    const [linkId, setLinkId] = useState();
    const [busy, setBusy] = useState('');
    const tools = useMemo(() => adminTools(id), [id]);
    const load = useCallback(async () => {
        if (!id) return;
        const r = await api.get(id);
        if (isOk(r)) setD(r.data); else message.error(errMsg(r, '加载失败'));
    }, [id]);
    useEffect(() => { setD(null); setPw(null); setLinkId(undefined); load(); }, [load]);

    const act = async (key, fn, ok = '已完成') => {
        setBusy(key);
        const r = await fn();
        setBusy('');
        if (isOk(r)) { message.success(ok); await load(); onChanged?.(); } else message.error(errMsg(r, '操作失败'));
    };

    return (
        <Drawer open={!!id} onClose={onClose} width={Math.min(720, window.innerWidth)} title={d ? `#${d.id} ${d.hostname}` : '详情'}
            extra={<Button icon={<SyncOutlined />} onClick={load} />}>
            {!d ? null : (
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                    {d.last_error && <Alert type="error" showIcon message="最近错误" description={d.last_error} />}
                    <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="状态"><StatusTag s={d.status} /> {d.upstream_status && <Text type="secondary">上游：{d.upstream_status}</Text>}</Descriptions.Item>
                        <Descriptions.Item label="用户">#{d.user_id}</Descriptions.Item>
                        <Descriptions.Item label="机型">{d.name}（{DC_LABEL[d.data_center] || d.data_center}，上游产品 #{d.upstream_product_id}）</Descriptions.Item>
                        <Descriptions.Item label="订阅">{d.subscription ? `#${d.subscription.id} · ${d.subscription.status} · ${money(d.subscription.price)} · 到期 ${String(d.subscription.expiry_date || '-').slice(0, 10)}` : '-'}</Descriptions.Item>
                        <Descriptions.Item label="上游订单">{d.upstream_order_id ? `#${d.upstream_order_id}（账单 #${d.upstream_invoice_id || '-'}，产品 ${d.upstream_product_ids || '-'}）` : '-'}</Descriptions.Item>
                        <Descriptions.Item label="上游服务器">{d.server_id ? `#${d.server_id}` : '未关联'}</Descriptions.Item>
                        <Descriptions.Item label="IP">{d.ip ? <Text copyable>{d.ip}</Text> : '-'}</Descriptions.Item>
                        <Descriptions.Item label="登录">
                            {d.has_password ? (pw === null
                                ? <Button size="small" icon={<EyeOutlined />} onClick={async () => { const r = await api.password(d.id); if (isOk(r)) setPw(r.data); else message.error(errMsg(r, '加载失败')); }}>查看密码</Button>
                                : <Space><Text copyable>{pw.user}</Text><Text copyable>{pw.password}</Text></Space>) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="配置">{arr(d.options).map((o) => <div key={o.group_id}><Text type="secondary">{o.group}：</Text>{o.addon}</div>)}</Descriptions.Item>
                        <Descriptions.Item label="时间">下单 {d.created_at} · 上游下单 {d.ordered_at || '-'} · 交付 {d.delivered_at || '-'}</Descriptions.Item>
                        {d.cancel_ticket_id && <Descriptions.Item label="取消工单">#{d.cancel_ticket_id}</Descriptions.Item>}
                        <Descriptions.Item label="供应商">{d.provider}{d.live?.status && <Text type="secondary"> · {d.live.status}{d.live.power ? ` · ${d.live.power}` : ''}</Text>}</Descriptions.Item>
                    </Descriptions>

                    <Space wrap>
                        {['failed', 'ordering'].includes(d.status) && (
                            <Popconfirm title="用同一个幂等键重新向上游下单（不会重复买）" onConfirm={() => act('retry', () => api.retry(d.id), '已重试')}>
                                <Button type="primary" loading={busy === 'retry'}>重试上游下单</Button>
                            </Popconfirm>
                        )}
                        {d.server_id && <Button loading={busy === 'refresh'} onClick={() => act('refresh', () => api.refresh(d.id), '已刷新')}>刷新交付信息</Button>}
                        {d.server_id && <Button loading={busy === 'on'} onClick={() => act('on', () => api.power(d.id, 'on'), '已开机')}>开机</Button>}
                        {d.server_id && (
                            <Popconfirm title="重启？" onConfirm={() => act('reboot', () => api.power(d.id, 'reboot'), '已重启')}>
                                <Button loading={busy === 'reboot'}>重启</Button>
                            </Popconfirm>
                        )}
                        {d.server_id && arr(d.capabilities).includes('kvm') && (
                            <Button loading={busy === 'kvm'} onClick={async () => {
                                setBusy('kvm'); const r = await tools.kvm(); setBusy('');
                                if (isOk(r) && r.data?.url) window.open(r.data.url, '_blank', 'noopener'); else message.error(errMsg(r, '操作失败'));
                            }}>KVM</Button>
                        )}
                        {d.server_id && (
                            <Popconfirm title="关机？" onConfirm={() => act('off', () => api.power(d.id, 'off'), '已关机')}>
                                <Button danger loading={busy === 'off'}>关机</Button>
                            </Popconfirm>
                        )}
                        {d.status === 'cancel_requested' && (
                            <Popconfirm title="确认已在上游面板取消、上游不再扣费？" onConfirm={() => act('cancelled', () => api.markCancelled(d.id), '已确认')}>
                                <Button loading={busy === 'cancelled'}>已在上游取消</Button>
                            </Popconfirm>
                        )}
                        {['failed', 'ordering'].includes(d.status) && !d.server_id && (
                            <Popconfirm title="退款到用户余额并结束订阅？" okButtonProps={{ danger: true }} onConfirm={() => act('refund', () => api.cancelRefund(d.id), '已取消退款')}>
                                <Button danger loading={busy === 'refund'}>取消并退款</Button>
                            </Popconfirm>
                        )}
                    </Space>

                    {d.upstream_error && <Alert type="warning" showIcon message="上游实时信息拉取失败" description={d.upstream_error} />}
                    {d.server_id && d.live && (
                        <DedicatedServerTools tools={tools} caps={d.capabilities} ips={d.live.ips} usage={d.live.bandwidth} onChanged={load} />
                    )}

                    {!d.server_id && !['cancelled', 'awaiting_payment'].includes(d.status) && (
                        <Card size="small" title="手动关联上游服务器">
                            <Space.Compact style={{ width: '100%' }}>
                                <InputNumber style={{ flex: 1 }} min={1} placeholder="上游 serverId（见「未关联的上游服务器」）" value={linkId} onChange={setLinkId} />
                                <Button type="primary" disabled={!linkId} loading={busy === 'link'} onClick={() => act('link', () => api.link(d.id, linkId), '已关联')}>关联</Button>
                            </Space.Compact>
                        </Card>
                    )}
                </Space>
            )}
        </Drawer>
    );
};

const ServersTab = ({ onSettings }) => {
    const [data, setData] = useState({ list: [], total: 0, counts: {} });
    const [status, setStatus] = useState('');
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(null);
    const load = useCallback(async () => {
        setLoading(true);
        const r = await api.list({ status, q, page });
        setLoading(false);
        if (isOk(r)) { setData(r.data); onSettings?.(r.data?.settings); } else message.error(errMsg(r, '加载失败'));
    }, [status, q, page, onSettings]);
    useEffect(() => { load(); }, [load]);
    const counts = data.counts || {};
    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                <Segmented value={status} onChange={(v) => { setStatus(v); setPage(1); }}
                    options={[{ label: '全部', value: '' }, ...Object.keys(STATUS_ZH).map((k) => ({ label: `${STATUS_ZH[k]}${counts[k] ? ` (${counts[k]})` : ''}`, value: k }))]} />
                <Input.Search allowClear placeholder="主机名 / IP / 邮箱 / ID" onSearch={(v) => { setQ(v); setPage(1); }} style={{ width: 260 }} />
                <Button icon={<ReloadOutlined />} onClick={load} />
            </Space>
            <Table rowKey="id" size="small" loading={loading} dataSource={arr(data.list)} scroll={{ x: 'max-content' }}
                onRow={(r) => ({ onClick: () => setOpen(r.id), style: { cursor: 'pointer' } })}
                pagination={{ current: page, total: data.total, pageSize: 20, onChange: setPage, showSizeChanger: false }}
                columns={[
                    { title: 'ID', dataIndex: 'id', width: 70 },
                    { title: '主机名', dataIndex: 'hostname', render: (v, r) => <Space direction="vertical" size={0}><Text strong>{v}</Text><Text type="secondary" style={{ fontSize: 12 }}>{r.name}</Text></Space> },
                    { title: '状态', dataIndex: 'status', render: (s, r) => <Space direction="vertical" size={0}><StatusTag s={s} />{r.last_error && <Text type="danger" style={{ fontSize: 12 }} ellipsis>{r.last_error}</Text>}</Space> },
                    { title: '用户', dataIndex: 'user_email', render: (v, r) => v || `#${r.user_id}` },
                    { title: '机房', dataIndex: 'data_center', render: (v) => DC_LABEL[v] || v },
                    { title: 'IP', dataIndex: 'ip', render: (v) => v || '-' },
                    { title: '上游', key: 'up', render: (_, r) => <Text type="secondary" style={{ fontSize: 12 }}>订单 {r.upstream_order_id || '-'} · 服务器 {r.server_id || '-'}</Text> },
                    { title: '下单时间', dataIndex: 'created_at', render: (v) => String(v || '').slice(0, 16) },
                ]} />
            <ServerDrawer id={open} onClose={() => setOpen(null)} onChanged={load} />
        </>
    );
};

const UnlinkedTab = () => {
    const [list, setList] = useState(null);
    const load = async () => {
        setList(null);
        const r = await api.unlinked();
        if (isOk(r)) setList(arr(r.data?.list)); else { setList([]); message.error(errMsg(r, '加载失败')); }
    };
    useEffect(() => { load(); }, []);
    return (
        <>
            <Alert style={{ marginBottom: 12 }} type="info" showIcon message="上游账户里存在、但还没关联到任何订单的服务器。打开对应订单，用 serverId 手动关联。" />
            <Table rowKey="id" size="small" loading={list === null} dataSource={arr(list)} pagination={false}
                columns={[
                    { title: 'serverId', dataIndex: 'id', render: (v) => <Text copyable>{v}</Text> },
                    { title: '标签', dataIndex: 'label' },
                    { title: '状态', dataIndex: 'status' },
                ]} />
            <Button style={{ marginTop: 12 }} icon={<ReloadOutlined />} onClick={load}>刷新</Button>
        </>
    );
};

/** 为用户下单：生成该用户的未付账单（线下收款到「账单」里登记已付） */
const OrderForUser = ({ model, onClose }) => {
    const [d, setD] = useState(null);
    const [uid, setUid] = useState();
    const [cycle, setCycle] = useState('monthly');
    const [choices, setChoices] = useState({});
    const [hostname, setHostname] = useState('');
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    useEffect(() => {
        setD(null); setQuote(null); setDone(null); setUid(undefined); setHostname('');
        if (!model) return;
        api.model(model.id).then((r) => {
            if (!isOk(r)) { message.error(errMsg(r, '加载失败')); return; }
            setD(r.data);
            const cs = Object.keys(r.data.cycles || {});
            setCycle(cs.includes('monthly') ? 'monthly' : cs[0]);
            setChoices(Object.fromEntries(arr(r.data.groups).map((g) => [g.id, g.default])));
        });
    }, [model]);
    useEffect(() => {
        if (!d) return;
        api.quote({ product_id: d.id, cycle, options: choices }).then((r) => setQuote(isOk(r) ? r.data : null));
    }, [d, cycle, choices]);
    const submit = async () => {
        setBusy(true);
        const r = await api.orderFor({ user_id: uid, product_id: d.id, cycle, options: choices, hostname: hostname.trim() });
        setBusy(false);
        if (isOk(r)) setDone(r.data); else message.error(errMsg(r, '下单失败'));
    };
    return (
        <Modal open={!!model} title={`为用户下单 · ${model?.name || ''}`} onCancel={onClose} destroyOnClose
            footer={done ? <Button type="primary" onClick={onClose}>完成</Button> : [
                <Button key="c" onClick={onClose}>取消</Button>,
                <Button key="o" type="primary" loading={busy} disabled={!uid || !quote} onClick={submit}>生成账单</Button>,
            ]}>
            {done ? (
                <Alert type="success" showIcon message={`已为用户 #${uid} 生成账单 #${done.invoice_id}（${money(done.amount)}）`}
                    description={<>用户付款后自动开通。线下收款请到 <a href="/billing/invoices">账单</a> 找到 #{done.invoice_id} 登记已付。服务器记录 #{done.server_id}。</>} />
            ) : !d ? <Spin /> : (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <InputNumber style={{ width: '100%' }} min={1} placeholder="用户 ID" value={uid} onChange={setUid} />
                    <Segmented block value={cycle} onChange={setCycle} options={Object.keys(d.cycles || {}).map((c) => ({ value: c, label: c }))} />
                    {arr(d.groups).filter((g) => !g.fixed && arr(g.options).length > 1).map((g) => (
                        <div key={g.id}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{g.name}</Text>
                            <Select style={{ width: '100%' }} showSearch optionFilterProp="label" value={choices[g.id]} onChange={(v) => setChoices((c) => ({ ...c, [g.id]: v }))}
                                options={arr(g.options).map((o) => ({ value: o.id, label: (o.prices?.[cycle]?.price || 0) > 0 ? `${o.name} (+${money(o.prices[cycle].price)})` : o.name }))} />
                        </div>
                    ))}
                    <Input placeholder="主机名（可选）" value={hostname} onChange={(e) => setHostname(e.target.value)} maxLength={253} />
                    {quote && <Text strong>首期 {money(quote.total)}，之后 {money(quote.recurring)} / {cycle}</Text>}
                </Space>
            )}
        </Modal>
    );
};

const CatalogTab = () => {
    const [d, setD] = useState(null);
    const load = async (fresh) => {
        setD(null);
        const r = await api.catalog(fresh);
        if (isOk(r)) setD(r.data); else { setD({ models: [] }); message.error(errMsg(r, '加载失败')); }
    };
    const [ordering, setOrdering] = useState(null);
    useEffect(() => { load(false); }, []);
    return (
        <>
            <Space style={{ marginBottom: 12 }}>
                <Text type="secondary">{d ? `有货 ${arr(d.models).length} 款（含未上架的 OneProvider）/ ReliableSite 上游共 ${d.total_upstream ?? '-'} 款 · 后台每 5 分钟自动同步，上次 ${d.refreshed_at ? new Date(d.refreshed_at * 1000).toLocaleString() : '-'}` : ''}</Text>
                <Button icon={<SyncOutlined />} onClick={() => load(true)}>立即刷新库存</Button>
            </Space>
            <Table rowKey="id" size="small" loading={d === null} dataSource={arr(d?.models)} scroll={{ x: 'max-content' }} pagination={{ pageSize: 30 }}
                columns={[
                    { title: '供应商', dataIndex: 'supplier', width: 110, filters: [{ text: 'ReliableSite', value: 'reliablesite' }, { text: 'OneProvider', value: 'oneprovider' }], onFilter: (v, m) => m.supplier === v, render: (v) => <Tag color={v === 'oneprovider' ? 'blue' : 'default'}>{v === 'oneprovider' ? 'OneProvider' : 'ReliableSite'}</Tag> },
                    { title: '机型 ID', dataIndex: 'id', width: 100 },
                    { title: '机型', dataIndex: 'name', render: (v, m) => <Space direction="vertical" size={0}><Text>{v}</Text><Text type="secondary" style={{ fontSize: 12 }}>{arr(m.specs).slice(0, 3).join(' · ')}</Text></Space> },
                    { title: '机房', dataIndex: 'data_center', render: (v, m) => <><CountryFlag code={m.country} />{DC_LABEL[v] || v}{m.dc_code && <Text type="secondary"> · {m.dc_code}</Text>}</> },
                    { title: '库存', dataIndex: 'stock', render: (v) => (v >= 10 ? '10+' : v) },
                    { title: '月成本', dataIndex: 'cost_monthly', render: money },
                    { title: '月售价', dataIndex: 'from', render: money, sorter: (a, b) => a.from - b.from },
                    { title: '流量', dataIndex: 'bandwidth', render: (v) => v || '-' },
                    { title: '周期', key: 'cycles', render: (_, m) => Object.keys(m.cycles || {}).length },
                    { title: '', key: 'op', width: 100, render: (_, m) => <Button size="small" onClick={() => setOrdering(m)}>为用户下单</Button> },
                ]} />
            <OrderForUser model={ordering} onClose={() => setOrdering(null)} />
        </>
    );
};

const SettingsTab = ({ settings, onSaved }) => {
    const [markup, setMarkup] = useState(settings?.markup);
    const [busy, setBusy] = useState(false);
    useEffect(() => { setMarkup(settings?.markup); }, [settings?.markup]);
    const save = async (data, ok) => {
        setBusy(true);
        const r = await api.settings(data);
        setBusy(false);
        if (isOk(r)) { message.success(ok); onSaved?.(r.data); } else message.error(errMsg(r, '保存失败'));
    };
    if (!settings) return null;
    return (
        <Descriptions column={1} bordered size="small" style={{ maxWidth: 620 }}>
            <Descriptions.Item label="对客户售卖">
                <Switch checked={settings.enabled} loading={busy} onChange={(v) => save({ enabled: v }, v ? '已开启售卖' : '已暂停售卖')} />
            </Descriptions.Item>
            <Descriptions.Item label="加价倍率">
                <Space>
                    <InputNumber min={1} max={5} step={0.05} value={markup} onChange={setMarkup} />
                    <Button type="primary" loading={busy} disabled={!markup || markup === settings.markup} onClick={() => save({ markup }, '已保存')}>保存</Button>
                    <Text type="secondary">售价 = 上游价 × 倍率，尾数取 .99；已售订单续费按下单时的价格</Text>
                </Space>
            </Descriptions.Item>
            <Descriptions.Item label="ReliableSite API 密钥">{settings.api_key_set ? <Tag color="green">已配置</Tag> : <Tag color="red">未配置</Tag>}</Descriptions.Item>
            {settings.oneprovider?.configured && <>
                <Descriptions.Item label="OneProvider 机型上架">
                    <Space>
                        <Switch checked={settings.oneprovider.enabled} loading={busy} onChange={(v) => save({ oneprovider_enabled: v }, v ? '已上架 OneProvider 机型' : '已下架 OneProvider 机型')} />
                        <Text type="secondary">只上架接口可管理的独立服务器，VPS 不会出现</Text>
                    </Space>
                </Descriptions.Item>
                <Descriptions.Item label="OneProvider 自动付款">
                    <Space wrap>
                        <Switch checked={settings.oneprovider.autopay} loading={busy} onChange={(v) => save({ oneprovider_autopay: v }, v ? '已开启自动付款' : '已关闭自动付款')} />
                        <Text type="secondary">用账户余额付首单和续费；余额不足时告警</Text>
                        {settings.oneprovider.credit != null && <Tag color={settings.oneprovider.credit < 100 ? 'orange' : 'green'}>余额 {money(settings.oneprovider.credit)}</Tag>}
                    </Space>
                </Descriptions.Item>
            </>}
        </Descriptions>
    );
};

const DedicatedManagement = () => {
    const [settings, setSettings] = useState(null);
    const onSettings = useCallback((s) => { if (s) setSettings(s); }, []);
    return (
        <Card title="独立服务器" extra={settings && (settings.enabled ? <Tag color="green">售卖中</Tag> : <Tag>未开售</Tag>)}>
            <Tabs items={[
                { key: 'servers', label: '服务器', children: <ServersTab onSettings={onSettings} /> },
                { key: 'unlinked', label: '未关联的上游服务器', children: <UnlinkedTab /> },
                { key: 'catalog', label: '机型与价格', children: <CatalogTab /> },
                { key: 'settings', label: '设置', children: <SettingsTab settings={settings} onSaved={setSettings} /> },
            ]} />
        </Card>
    );
};

export default DedicatedManagement;
